import { useEffect, type ReactNode } from 'react';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { cn } from '@rocco/utils/cn';
// Imported from the leaf module rather than the island's barrel: this only needs the
// surface token, and the barrel would pull the whole island (and framer-motion) in.
import { ISLAND_GLASS_LIGHT } from '~/ui-rocco/pages/detail-shared/magic-island/styles';

/**
 * How many pages' own staff islands are on screen. The shell's feedback-only
 * island stands down while any is, so a detail page shows one island with its
 * tools and the feedback control in it, rather than two in the same corner.
 */
const staffIslandHostsAtom = atom(0);

/** Counts this island as on screen for as long as `active` holds. */
export const useRegisterStaffIsland = (active: boolean) => {
    const setHosts = useSetAtom(staffIslandHostsAtom);
    useEffect(() => {
        if (!active) return;
        setHosts((count) => count + 1);
        return () => setHosts((count) => count - 1);
    }, [active, setHosts]);
};

/** Whether a page has put up its own staff island. */
export const useStaffIslandHosted = () =>
    useAtomValue(staffIslandHostsAtom) > 0;

interface StaffIslandFrameProps {
    /** Hide the island without unmounting it (the page's fixed chrome is down). */
    hidden?: boolean;
    children: ReactNode;
}

/**
 * The staff island's frame, bottom left: the frosted pill every staff control
 * sits in, whichever page or shell put it up.
 */
export const StaffIslandFrame = ({
    hidden,
    children,
}: StaffIslandFrameProps) => (
    <div
        className={cn(
            // Sits in the same band as the Magic Island (bottom-8) and borrows its
            // frosted-glass surface, so the two read as one system rather than two
            // unrelated floating controls.
            ISLAND_GLASS_LIGHT,
            'fixed bottom-8 left-8 z-fixed flex flex-row items-center gap-2 rounded-tile p-2 transition-[opacity,visibility] duration-300',
            // The page's standard corner, on the frame and on everything
            // inside it. The controls are aria `Button`s whose circular
            // radius is set by Fela at a specificity plain utilities lose
            // to, so these have to be marked important to land.
            '[&_button]:rounded-tile! [&_img]:rounded-tile!',
            hidden && 'invisible opacity-0',
        )}
    >
        {children}
    </div>
);
